import type { ChangeEvent, DragEvent, KeyboardEvent, ReactNode } from "react";
import styles from "./FolderDropZone.module.css";

type FolderDropZoneProps = {
  folderInputId: string;
  folderName: string | null;
  caption: ReactNode;
  inputLabel: string;
  // 드롭한 트리를 순회하는 중이다. 큰 폴더는 수 초 걸릴 수 있다.
  scanning: boolean;
  dragActive: boolean;
  disabled: boolean;
  onFolderInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onClick: () => void;
  onDragEnter: (event: DragEvent<HTMLElement>) => void;
  onDragOver: (event: DragEvent<HTMLElement>) => void;
  onDragLeave: (event: DragEvent<HTMLElement>) => void;
  onDrop: (event: DragEvent<HTMLElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
};

// 폴더를 선택하려면 `webkitdirectory` 와 `directory` 가 필요하다. 이 둘은 React 표준 prop 이
// 아니므로 소문자 DOM attribute 로 함께 전달한다.
const folderInputAttributes = { webkitdirectory: "", directory: "" } as Record<string, string>;

// 데이터셋 폴더 드롭존(숨김 folder input, 드래그/클릭/키보드 위임). 스캔 실행과 검증은
// 호출부 컨트롤러가 소유한다. 이 컴포넌트는 표시와 입력 이벤트 전달만 한다.
export function FolderDropZone({
  folderInputId,
  folderName,
  caption,
  inputLabel,
  scanning,
  dragActive,
  disabled,
  onFolderInputChange,
  onClick,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
  onKeyDown,
}: FolderDropZoneProps): ReactNode {
  const className = [
    styles.dropZone,
    dragActive ? styles.dropZoneActive : "",
    disabled ? styles.dropZoneDisabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <input
        id={folderInputId}
        className={styles.folderInput}
        type="file"
        multiple
        {...folderInputAttributes}
        disabled={disabled}
        aria-label={inputLabel}
        onChange={onFolderInputChange}
      />
      <div
        className={className}
        role="button"
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={disabled ? undefined : onClick}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onKeyDown={onKeyDown}
      >
        <span className={styles.dropZoneIcon} aria-hidden="true">
          <FolderUploadIcon />
        </span>
        {scanning ? (
          <span className={styles.dropZoneText}>폴더를 확인하고 있습니다.</span>
        ) : folderName === null ? (
          <>
            <span className={styles.dropZoneText}>폴더를 끌어다 놓거나 폴더 선택</span>
            <span className={styles.dropZoneMeta}>{caption}</span>
          </>
        ) : (
          <>
            <span className={styles.dropZoneText}>{folderName}</span>
            <span className={styles.dropZoneMeta}>{caption}</span>
          </>
        )}
      </div>
    </>
  );
}

function FolderUploadIcon() {
  return (
    <svg aria-hidden="true" width="1em" height="1em" viewBox="0 0 20 20">
      <path
        d="M3 6.5A1.5 1.5 0 0 1 4.5 5h3l1.5 2h6.5A1.5 1.5 0 0 1 17 8.5v5A1.5 1.5 0 0 1 15.5 15h-11A1.5 1.5 0 0 1 3 13.5v-7Zm7 6v-4m0 0-2 2m2-2 2 2"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  );
}
